import { inject, injectable } from "inversify";
import axios, { CancelToken } from "axios";
import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";
import { TYPES } from "@utils/inversifyJS/types";
import type { IAuthenticatedRequestHandlerService } from "@application/contracts/services";
import type { IRawFileRepository } from "@domain/repositories/IRawFileRepository";
import { PagedBatchRawFileList, RawFileUsage } from "@domain/schemas/BI";
import { deleteProjectStorageFile } from "./ProjectStorageFileDeletion";

@injectable()
export class RawFileRepository implements IRawFileRepository {
  private static readonly CHUNK_SIZE_BYTES = 8 * 1024 * 1024;
  private static readonly RAW_ROOT_PREFIXES = ["raw", "raw_preview", "raw_staging"] as const;

  @inject(TYPES.IAuthenticatedRequestHandlerService)
  private _requestHandlerService!: IAuthenticatedRequestHandlerService;

  /**
   * Cleans up separators only. Does NOT strip the raw container root.
   *
   * The listing call must preserve whatever folder it was handed, because
   * {@link FileExplorerPage} probes container-root aliases (`raw`,
   * `raw_preview`, `raw_staging`) by varying that prefix. Stripping it here
   * collapses every probe onto an identical URL, so the container-prefixed
   * form can never be requested and the listing silently returns nothing.
   */
  private normalizeFolderPath(folder?: string | null): string {
    return String(folder ?? "")
      .replaceAll("\\", "/")
      .replace(/^\/+/, "")
      .replaceAll(/\/+/g, "/");
  }

  /**
  Builds the `{folder}/{fileName}` path the delete and usage routes take as a catch-all URL segment.

  Each segment is encoded individually: the separators must stay literal slashes for the catch-all to
  match, so encoding the joined path would turn them into `%2F` and the route would not bind.
  */
  private buildRawFilePath(fileName: string, folder?: string): string {
    return [...(folder ? folder.split("/") : []), fileName]
      .filter((segment) => segment.length > 0)
      .map((segment) => encodeURIComponent(segment))
      .join("/");
  }

  /**
   * Container-relative folder, used by the upload and existence-check APIs,
   * which address blobs relative to the raw container rather than by full path.
   */
  private normalizeFolder(folder?: string | null): string {
    const normalizedFolder = this.normalizeFolderPath(folder);

    if (!normalizedFolder) {
      return "";
    }

    for (const prefix of RawFileRepository.RAW_ROOT_PREFIXES) {
      if (normalizedFolder === prefix) {
        return "";
      }

      if (normalizedFolder.startsWith(`${prefix}/`)) {
        const withoutPrefix = normalizedFolder.slice(prefix.length + 1);
        return withoutPrefix;
      }
    }

    return normalizedFolder;
  }

  async getRawFiles(projectId: string, folder: string, sourcePath?: string | null): Promise<PagedBatchRawFileList> {
    const normalizedFolder = this.normalizeFolderPath(folder);
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFiles}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const response = await this._requestHandlerService.handleRequestAsync<PagedBatchRawFileList>(() =>
      axios.get<PagedBatchRawFileList>(url, { params: { folder: normalizedFolder, sourcePath: sourcePath || undefined } }),
    );

    const responseWithAlternativeCasing = response as unknown as {
      list?: PagedBatchRawFileList["list"];
      List?: PagedBatchRawFileList["list"];
      items?: PagedBatchRawFileList["list"];
      Items?: PagedBatchRawFileList["list"];
      value?: unknown;
      Value?: unknown;
      data?: unknown;
      Data?: unknown;
      $values?: unknown;
      [key: string]: unknown;
    };

    const unwrapCollectionCandidate = (candidate: unknown): unknown[] => {
      if (Array.isArray(candidate)) {
        return candidate;
      }

      if (!candidate || typeof candidate !== "object") {
        return [];
      }

      const obj = candidate as {
        list?: unknown;
        List?: unknown;
        items?: unknown;
        Items?: unknown;
        value?: unknown;
        Value?: unknown;
        data?: unknown;
        Data?: unknown;
        $values?: unknown;
      };

      const nestedCandidates = [obj.list, obj.List, obj.items, obj.Items, obj.value, obj.Value, obj.data, obj.Data, obj.$values];

      for (const nestedCandidate of nestedCandidates) {
        const unwrapped = unwrapCollectionCandidate(nestedCandidate);
        if (unwrapped.length > 0) {
          return unwrapped;
        }
      }

      return [];
    };

    const listCandidate = unwrapCollectionCandidate(Array.isArray(response) ? response : responseWithAlternativeCasing);

    const normalizedList = (listCandidate ?? []).map((rawFile) => {
      const rawFileBase = rawFile && typeof rawFile === "object" ? (rawFile as Record<string, unknown>) : {};
      const rawFileWithAlternativeCasing = rawFile as {
        location?: string;
        Location?: string;
        fileName?: string;
        FileName?: string;
        path?: string;
        Path?: string;
        sizeInBytes?: number;
        SizeInBytes?: number;
        fileCount?: number | null;
        FileCount?: number | null;
        [key: string]: unknown;
      };

      const path = rawFileWithAlternativeCasing.path ?? rawFileWithAlternativeCasing.Path;
      const normalizedPath = typeof path === "string" ? path.replaceAll("\\", "/") : "";

      const explicitLocationCandidate = rawFileWithAlternativeCasing.location ?? rawFileWithAlternativeCasing.Location;
      const explicitFileNameCandidate = rawFileWithAlternativeCasing.fileName ?? rawFileWithAlternativeCasing.FileName;

      const explicitLocation = typeof explicitLocationCandidate === "string" && explicitLocationCandidate.trim().length > 0 ? explicitLocationCandidate : null;
      const explicitFileName = typeof explicitFileNameCandidate === "string" && explicitFileNameCandidate.trim().length > 0 ? explicitFileNameCandidate : null;

      const locationFromPath = normalizedPath ? (normalizedPath.endsWith("/") ? normalizedPath.replace(/\/+$/, "") : normalizedPath.split("/").slice(0, -1).join("/")) : "";

      const fileNameFromPath = normalizedPath ? (normalizedPath.endsWith("/") ? "" : (normalizedPath.split("/").pop() ?? "")) : "";

      const location = explicitLocation ?? locationFromPath;
      const fileName = explicitFileName ?? fileNameFromPath;
      const sizeInBytesCandidate = rawFileWithAlternativeCasing.sizeInBytes ?? rawFileWithAlternativeCasing.SizeInBytes;
      const fileCountCandidate = rawFileWithAlternativeCasing.fileCount ?? rawFileWithAlternativeCasing.FileCount;
      const sizeInBytes = typeof sizeInBytesCandidate === "number" && Number.isFinite(sizeInBytesCandidate) ? sizeInBytesCandidate : 0;
      const fileCount = typeof fileCountCandidate === "number" && Number.isFinite(fileCountCandidate) ? fileCountCandidate : null;

      return {
        ...rawFileBase,
        location: String(location),
        fileName: String(fileName),
        sizeInBytes,
        fileCount,
      };
    });

    if (Array.isArray(response)) {
      return { list: normalizedList } as PagedBatchRawFileList;
    }

    return {
      ...responseWithAlternativeCasing,
      list: normalizedList,
    } as PagedBatchRawFileList;
  }

  async uploadRawFile(projectId: string, file: File, folder: string, rootPath?: string | null, onProgress?: (progress: number) => void, cancelToken?: CancelToken): Promise<void> {
    const initUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFilesUploadInit}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const chunkUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFilesUploadChunk}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const completeUrl = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFilesUploadComplete}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const totalChunks = Math.max(1, Math.ceil(file.size / RawFileRepository.CHUNK_SIZE_BYTES));
    const normalizedFolder = this.normalizeFolder(folder);

    const uploadId = await this._requestHandlerService.handleRequestAsync<string>(() =>
      axios.post<string>(
        initUrl,
        {
          fileName: file.name,
          folder: normalizedFolder,
          fileLength: file.size,
          rootPath: rootPath || undefined,
        },
        {
          timeout: 24 * 60 * 60 * 1000,
          cancelToken,
        },
      ),
    );

    const partTokens: string[] = [];
    let completedBytes = 0;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * RawFileRepository.CHUNK_SIZE_BYTES;
      const end = Math.min(start + RawFileRepository.CHUNK_SIZE_BYTES, file.size);
      const chunk = file.slice(start, end);
      const partNumber = chunkIndex + 1;

      const partToken = await this._requestHandlerService.handleRequestAsync<string>(() =>
        axios.post<string>(chunkUrl, chunk, {
          params: {
            uploadId,
            partNumber,
            fileName: file.name,
            folder: normalizedFolder,
            rootPath: rootPath || undefined,
          },
          headers: {
            "Content-Type": "application/octet-stream",
          },
          timeout: 24 * 60 * 60 * 1000,
          cancelToken,
          onUploadProgress: (progressEvent) => {
            if (!onProgress) {
              return;
            }

            const loaded = Math.min(completedBytes + (progressEvent.loaded ?? 0), file.size);
            const percentCompleted = Math.min(99, Math.round((loaded * 100) / (file.size || 1)));
            onProgress(percentCompleted);
          },
        }),
      );

      partTokens.push(partToken);
      completedBytes = end;

      if (!onProgress) continue;
      const percentCompleted = chunkIndex === totalChunks - 1 ? 99 : Math.min(99, Math.round((completedBytes * 100) / (file.size || 1)));
      onProgress(percentCompleted);
    }

    await this._requestHandlerService.handleRequestAsync<void>(() =>
      axios.post<void>(
        completeUrl,
        {
          uploadId,
          partTokens,
          fileName: file.name,
          folder: normalizedFolder,
          md5Hash: null,
          rootPath: rootPath || undefined,
        },
        {
          timeout: 24 * 60 * 60 * 1000,
          cancelToken,
        },
      ),
    );
  }

  async checkFileExists(projectId: string, fileName: string, rootPath?: string | null, folder?: string): Promise<number | null> {
    const normalizedFolder = this.normalizeFolder(folder);
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.checkFile}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const response = await this._requestHandlerService.handleRequestAsync<number | null>(() =>
      axios.get<number | null>(url, { params: { fileName, folder: normalizedFolder || undefined, rootPath: rootPath || undefined } }),
    );
    return response;
  }

  async checkFilesExist(projectId: string, fileNames: string[], rootPath?: string | null, folder?: string): Promise<Record<string, number | null>> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.checkFiles}`.replace("{projectId}", () => encodeURIComponent(projectId));
    const payload = { fileNames, folder: this.normalizeFolder(folder), rootPath: rootPath || undefined };
    const response = await this._requestHandlerService.handleRequestAsync<Record<string, number | null>>(() => axios.post<Record<string, number | null>>(url, payload));
    return response;
  }

  async getRawFileUsage(projectId: string, fileName: string, folder?: string, isFolder?: boolean, rootPath?: string | null): Promise<RawFileUsage> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFilesUsage}/${this.buildRawFilePath(fileName, folder)}`.replace("{projectId}", () => encodeURIComponent(projectId));
    return this._requestHandlerService.handleRequestAsync<RawFileUsage>(() => axios.get<RawFileUsage>(url, { params: { isFolder, rootPath: rootPath ?? undefined } }));
  }

  async deleteRawFile(projectId: string, fileName: string, folder?: string, isFolder?: boolean, rootPath?: string | null): Promise<boolean> {
    const url = `${runtimeConfig.API_BASE_URL}${API_ENDPOINTS.rawFilesDelete}/${this.buildRawFilePath(fileName, folder)}`.replace("{projectId}", () => encodeURIComponent(projectId));
    return deleteProjectStorageFile(this._requestHandlerService, url, { isFolder, rootPath: rootPath ?? undefined });
  }
}
